// Responsive layout for the Local LLM endpoints list. The list adapts to the width
// it actually has (not the window): the full table when there's room, a condensed
// four-column table when it's tighter, and one card per endpoint when it's narrow —
// so no column is ever cut off and nothing needs sideways scrolling (staging
// 2026-10-09: the Status column was clipped once the window narrowed).
import { COLORS } from "../../theme/colors";

// Widths (px of the list itself) where the layout steps down.
export const LAYOUT_BREAKPOINTS = { table: 980, compact: 620 };

/** "table" | "compact" | "cards" for a list this wide. Unmeasured (0) → "table". */
export function endpointLayout(width) {
  if (!width || width >= LAYOUT_BREAKPOINTS.table) return "table";
  return width >= LAYOUT_BREAKPOINTS.compact ? "compact" : "cards";
}

const th = {
  textAlign: "left", padding: "10px 10px", fontSize: 11, fontWeight: 600, color: COLORS.muted,
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const td = { padding: "10px 10px", fontSize: 13, color: COLORS.text, verticalAlign: "middle" };
const right = { textAlign: "right", paddingRight: 14 };
const dim = { color: COLORS.muted };
// a long URL breaks anywhere; its width follows the font, not a pixel size
const url = { fontFamily: "monospace", fontSize: 11, overflowWrap: "anywhere", maxWidth: "30ch" };

const HEADERS = {
  table: ["Slug", "Name", "Base URL", "Default Model", "Models", "Health", "Status"],
  compact: ["Endpoint", "Model", "Status"],
};

/** Column headings for the table layouts. */
export function EndpointTableHead({ layout }) {
  return (
    <thead>
      <tr style={{ background: COLORS.bgElevated }}>
        {HEADERS[layout].map((h) => <th key={h} style={th}>{h}</th>)}
        <th style={{ ...th, ...right }}>Actions</th>
      </tr>
    </thead>
  );
}

const modelText = (row) => row.default_model || <span style={dim}>—</span>;
const countText = (row) => `${row.cached_models?.length ?? 0} model${(row.cached_models?.length ?? 0) === 1 ? "" : "s"}`;

/** One endpoint in the given layout. ``parts`` are the cells that differ between a
 * hosted model and one you run yourself: {health, status, actions}. */
export function EndpointLayout({ row, layout, parts }) {
  const fade = { opacity: row.enabled ? 1 : 0.55 };
  if (layout === "cards") return <EndpointCard row={row} parts={parts} fade={fade} />;
  const rowStyle = { borderTop: `1px solid ${COLORS.border}`, ...fade };
  const actions = <td style={{ ...td, ...right }}>{parts.actions}</td>;
  if (layout === "compact") {
    const who = <div style={{ display: "grid", gap: 3 }}><code style={{ fontSize: 12 }}>{row.endpoint_id}</code><span>{row.display_name}</span><span style={{ ...url, color: COLORS.textDim }}>{row.base_url}</span></div>;
    const model = <div style={{ display: "grid", gap: 3 }}><span>{modelText(row)}</span><span style={{ fontSize: 12, ...dim }}>{countText(row)}</span></div>;
    const state = <div style={{ display: "grid", gap: 6, justifyItems: "start" }}>{parts.health}{parts.status}</div>;
    return <tr data-layout="compact" style={rowStyle}><td style={td}>{who}</td><td style={td}>{model}</td><td style={td}>{state}</td>{actions}</tr>;
  }
  return (
    <tr data-layout="table" style={rowStyle}>
      <td style={td}><code style={{ fontSize: 12 }}>{row.endpoint_id}</code></td>
      <td style={td}>{row.display_name}</td>
      <td style={{ ...td, ...url }}>{row.base_url}</td>
      <td style={td}>{modelText(row)}</td>
      <td style={td}>{row.cached_models?.length ?? 0}</td>
      <td style={td}>{parts.health}</td>
      <td style={td}>{parts.status}</td>
      {actions}
    </tr>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(80px, 30%) 1fr", gap: 10, alignItems: "start" }}>
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", ...dim }}>{label}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

function EndpointCard({ row, parts, fade }) {
  return (
    <div role="listitem" data-layout="cards" data-testid="endpoint-card"
      style={{ display: "grid", gap: 10, padding: 14, borderTop: `1px solid ${COLORS.border}`, ...fade }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 10px" }}>
        <code style={{ fontSize: 12 }}>{row.endpoint_id}</code>
        <b style={{ fontSize: 14 }}>{row.display_name}</b>
      </div>
      <Field label="Base URL"><span style={url}>{row.base_url}</span></Field>
      <Field label="Model">{modelText(row)} <span style={{ fontSize: 12, ...dim }}>· {countText(row)}</span></Field>
      <Field label="Health">{parts.health}</Field>
      <Field label="Status">{parts.status}</Field>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 6 }}>{parts.actions}</div>
    </div>
  );
}
